import { FC, useMemo } from "react";
import { IWidgetTemplate } from "../../../interfaces/widget-template";
import { ReachClick } from "../../../utils/core";
import { resolveWidgetV2Style } from "./widgetV2Styles";

/**
 * Industry Compare (v2) — admin-preview-only. Real embed markup lives in
 * widget-v2.js (renderComparison), which fetches the same
 * GET /widgets/information/company/benchmark this page's widgets.tsx calls
 * (getBenchmarkByIdApi, only for templateId 25). Neither mounts anything
 * when the company has no primary category set (benchmark.category is
 * null) — there is nothing valid to compare against.
 */
const WidgetTemplate25: FC<IWidgetTemplate> = ({ company, score, benchmark, style }) => {
  const companyId = company?.companyId;
  const resolvedStyle = resolveWidgetV2Style("comparison", style);
  const payload = useMemo(() => ({ companyId, widgetId: 25 as const }), [companyId]);
  const profileUrl = useMemo(
    () => `https://www.trustfinance.com/companies/${companyId}?utm_source=widget&utm_medium=dom&utm_campaign=widget-v2-comparison`,
    [companyId],
  );

  if (!benchmark?.category || benchmark.industryAverageScore == null) return null;

  const yourScore = score?.score ?? 0;
  const industryScore = benchmark.industryAverageScore;
  const categoryName = benchmark.category.name;

  if (resolvedStyle === "rank-card") {
    const better = yourScore >= industryScore;
    return (
      <div className="d-flex justify-content-center">
        <a
          href={profileUrl}
          target="_blank"
          rel="noopener"
          onClick={() => ReachClick(payload)}
          className="text-center"
          style={{
            textDecoration: "none",
            color: "#1c1c2e",
            border: "1px solid #e7e8ec",
            borderRadius: 8,
            background: "#fff",
            padding: "12px 16px",
            maxWidth: "100%",
          }}
        >
          <b style={{ display: "block", fontSize: 24, lineHeight: 1, color: better ? "#006bf1" : "#f5a623" }}>{yourScore}</b>
          <div className="mt-1 text-truncate" style={{ fontSize: 10.5, color: "#6b6b85", maxWidth: 180 }}>
            vs {industryScore} avg in {categoryName}
          </div>
        </a>
      </div>
    );
  }

  // standard — Bars
  const rows: [string, number, string][] = [
    ["You", yourScore, "#006bf1"],
    ["Industry avg", industryScore, "#9aa0b4"],
  ];

  return (
    <div className="d-flex justify-content-center">
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener"
        onClick={() => ReachClick(payload)}
        style={{ textDecoration: "none", color: "#1c1c2e", display: "block", maxWidth: 220, width: "100%" }}
      >
        <div className="text-truncate" style={{ fontSize: 9.5, color: "#9aa0b4" }}>vs {categoryName}</div>
        <div className="d-grid mt-1" style={{ gap: 6 }}>
          {rows.map(([label, value, color]) => (
            <div key={label} className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
              <span style={{ width: 78, flexShrink: 0, fontSize: 10.5, color: "#6b6b85" }}>{label}</span>
              <div style={{ flex: 1, minWidth: 0, height: 8, borderRadius: 999, background: "#f7f8fa", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${Math.max(0, Math.min(value, 100))}%`,
                    borderRadius: 999,
                    background: color,
                  }}
                />
              </div>
              <b style={{ width: 22, flexShrink: 0, textAlign: "right", fontSize: 10.5 }}>{value}</b>
            </div>
          ))}
        </div>
      </a>
    </div>
  );
};

export default WidgetTemplate25;
